/**
 * App.jsx - Main Application Component
 * 
 * This is the root component of the React application that sets up the routing structure
 * and layout for the entire website. It uses React Router v6 for navigation.
 * 
 * Dependencies:
 * - react: Core React library
 * - react-router-dom: For client-side routing
 * - @/components/: Reusable UI components
 * - @/pages/: Page components for each route
 */

import React from 'react';
// react-router-dom components for client-side routing
import { Routes, Route, Outlet } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';

// Layout components
import Navbar from './components/Navbar';  // Navigation bar component
import Footer from './components/Footer';    // Footer component

// Page components - One for each route
import HomePage from './pages/HomePage';      // Homepage component
import AboutPage from './pages/AboutPage';    // About Us page
import ServicesPage from './pages/ServicesPage'; // Services listing page
import PricingPage from './pages/PricingPage';  // Pricing information page
import BookingPage from './pages/BookingPage';  // Service booking page
import ContactPage from './pages/ContactPage';  // Contact information page
import BlogPage from './pages/BlogPage';      // Blog/News page
import GalleryPage from './pages/GalleryPage'; // Photo gallery page
import ComingSoon from './pages/ComingSoon';   // Placeholder for upcoming features
import NotFoundPage from './pages/NotFoundPage'; // 404 error page

// Global styles
import './App.css'; // Contains global styles and CSS resets

/**
 * AppLayout Component
 * 
 * A layout wrapper component that provides the basic structure for all pages.
 * It includes the Navbar at the top, main content area (where page content renders),
 * and Footer at the bottom.
 * 
 * Uses flexbox to ensure the footer stays at the bottom of the viewport
 * even when page content is minimal.
 */
const AppLayout = () => {
  return (
    <div style={{ 
      display: 'flex', 
      flexDirection: 'column', 
      minHeight: '100vh' // Ensures the layout takes at least the full viewport height
    }}>
      {/* Persistent navigation bar across all pages */}
      <Navbar />
      
      {/* Main content area that grows to fill available space */}
      <main style={{ flex: '1' }}> {/* flex: 1 makes this element grow to fill space */}
        {/* Outlet renders the matched child route component */}
        <Outlet />
      </main>
      
      {/* Persistent footer across all pages */}
      <Footer />
    </div>
  );
};

/**
 * Main App Component
 * 
 * Sets up the application routing using React Router v6.
 * Defines all the routes for the application and maps them to their respective components.
 * The AppLayout component wraps all routes to provide consistent layout across pages.
 */
function App() {
  return (
    <HelmetProvider>
      <Routes>
        {/* Root route that uses AppLayout as the layout wrapper */}
        <Route path="/" element={<AppLayout />}>
          {/* Index route - renders when path is exactly "/" */}
          <Route index element={<HomePage />} />
          
          {/* Static routes */}
          <Route path="about" element={<AboutPage />} />
          <Route path="services" element={<ServicesPage />} />
          <Route path="pricing" element={<PricingPage />} />
          <Route path="booking" element={<BookingPage />} />
          <Route path="contact" element={<ContactPage />} />
          <Route path="blog" element={<BlogPage />} />
          
          {/* Dynamic route for individual blog posts */}
          <Route path="blog/:slug" element={<ComingSoon />} />
          
          <Route path="gallery" element={<GalleryPage />} />
          
          {/* Placeholder routes for future features */}
          <Route path="faq" element={<ComingSoon />} />
          <Route path="coming-soon" element={<ComingSoon />} />
          
          {/* Catch-all route for 404 errors - must be the last route */}
          <Route path="*" element={<NotFoundPage />} />
        </Route>
      </Routes>
    </HelmetProvider>
  );
}

export default App;
